import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Alert, Container, Row, Col, Card } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FormGroupCheck } from '@components/Base';
import HeaderSecondary from '@components/HeaderSecondary';
import AbaPayway from '@components/AbaPayway';
import FakePayment from '@components/FakePayment';
import { QuotePremiumCardBody } from '@components/Feature/Quote';
import { PaymentMethodToggleControl } from '@components/Feature/PaymentMethod';
import { PaymentMethods } from '@models/PaymentMethod';
import { routePrivacyPolicyPage } from '@components/PrivacyPolicyPage';
import { routeTermsAndConditionsPage } from '@components/TermsAndConditionsPage';

import { paymentTransactionCreate, PaymentTransactionCreatePayload } from './action';
import { useBillingFetch, useGeneralOrderFetch, useValidationSchema, useInitialValues } from './hook';
import { selectGeneralOrderPayment } from './selector';
import { FormValues } from './type';

const GeneralOrderPaymentPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const generalOrder = useGeneralOrderFetch();
  const billing = useBillingFetch();
  const validationSchema = useValidationSchema();
  const initialValues = useInitialValues();
  const { paymentTransaction } = useSelector(selectGeneralOrderPayment);
  const [bypass, setBypass] = useState(false);
  const PaymentGateway = bypass ? FakePayment : AbaPayway;

  if (!generalOrder) return null;
  if (!billing) return null;

  const { descriptionHtml } = generalOrder;

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: PaymentTransactionCreatePayload = { billingId: billing.id, ...values };
    dispatch(paymentTransactionCreate(payload));
    setSubmitting(false);
  };

  return (
    <>
      <HeaderSecondary />
      <Container className="my-3">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card>
              <QuotePremiumCardBody {...generalOrder} />

              <Card.Body>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnMount
                >
                  {({ isValid }) => (
                    <Form>
                      <Card.Title>{t('paymentOption.title')}</Card.Title>
                      <Row className="gy-3 gy-lg-0 mb-5">
                        <Col>
                          <PaymentMethodToggleControl value={PaymentMethods.AbaPay} />
                        </Col>
                      </Row>

                      <FormGroupCheck
                        groupProps={{ className: 'mb-5' }}
                        name="agreeToTermsAndConditions"
                        label={
                          <Trans
                            i18nKey="iAgreeToTermAndPolicy"
                            t={t}
                            components={{
                              term: <Link to={routeTermsAndConditionsPage()} target="_blank" rel="noreferrer" />,
                              policy: <Link to={routePrivacyPolicyPage()} target="_blank" rel="noreferrer" />,
                            }}
                          />
                        }
                      />

                      {process.env.RAILS_ENV !== 'production' && (
                        <FormGroupCheck
                          groupProps={{ className: 'mb-5' }}
                          id="bypass"
                          name="bypass"
                          label="Bypass payment"
                          onChange={() => setBypass(true)}
                        />
                      )}

                      <Button type="submit" variant="primary" className="btn-lg w-100" disabled={!isValid}>
                        <span className="me-2">{t('common:paySecurely')}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="fa-fw" />
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Card.Body>

              <Card.Body>
                <Card.Title className="text-danger">{t('common:declaration')}</Card.Title>
                <Alert variant="danger" className="mb-0">
                  {parse(descriptionHtml)}
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {paymentTransaction && <PaymentGateway paymentTransactionId={paymentTransaction.id} />}
    </>
  );
};

export { GeneralOrderPaymentPage };
